import React, { useState } from "react"
import { Modal, ModalHeader, ModalFooter, Button, ModalBody, Input } from "reactstrap"
import { LabeledCheckboxInput } from 'web/components/generic/LabeledCheckbox'
import { useFocusOnMount } from 'web/hooks/useFocusOnMount'
import { useKeyShortcut } from 'web/hooks/useKeyShortcut'
import { addUniqueToArray, removeFromArray } from 'web/utils/utils'
import { ModalContainerProps } from "./_ModalContainer"

export type PromptModalProps = {
    title: string
    message?: string | JSX.Element
    defaultText?: string

    onConfirm: (text: string | undefined, checked?: string[]) => void

    checkboxes?: {
        value: string
        label: string
    }[]
}

export const PromptModal: React.FC<PromptModalProps & ModalContainerProps> = ({
    title, message, defaultText, checkboxes, onConfirm, closeSelf
}) => {

    const [text, setText] = useState<string>(defaultText ?? '')
    const inputRef = useFocusOnMount({ selectText: true })

    const [checked, setChecked] = useState<string[]>([])

    const doSave = () => {
        onConfirm(text.trim().length <= 0 ? undefined : text.trim(), checked)
        closeSelf()
    }

    useKeyShortcut({
        key: "s",
        modifier: "ctrl",
        callback: doSave,
    })

    return (
        <Modal
            isOpen
            fade={false}
            toggle={closeSelf}
            backdrop={"static"}
        >

            <ModalHeader>
                {title}
            </ModalHeader>

            <ModalBody className="p-3 m-0 bg-light">
                {message !== "" &&
                    <div className="small pb-3">
                        {message}
                    </div>
                }

                <Input
                    bsSize="sm"
                    innerRef={inputRef}
                    type="text"
                    value={text}
                    onChange={e => setText(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" ? doSave() : null}
                    data-test={"prompt-input"}
                />

                {checkboxes != null &&
                    <div className="mt-3 space-my-2">

                        {checkboxes?.map(cb => (
                            <LabeledCheckboxInput
                                key={cb.value}
                                small
                                label={cb.label}
                                checked={checked.includes(cb.value)}
                                onChange={(isChecked) => {
                                    if (isChecked) {
                                        setChecked(addUniqueToArray(checked, cb.value))
                                    } else {
                                        setChecked(removeFromArray(checked, cb.value))
                                    }
                                }}
                            />
                        ))}

                    </div>
                }
            </ModalBody>

            <ModalFooter className="d-flex justify-content-end space-mx-2" >
                <Button
                    size="sm"
                    onClick={doSave}
                    color={'success'}
                >
                    {"Confirm"}
                </Button>

                <Button
                    outline
                    size="sm"
                    onClick={closeSelf}
                    title={"Cancel (esc)"}
                >
                    {"Cancel"}
                </Button>
            </ModalFooter>
        </Modal>
    )
}
